/* assets/littleg-kid-location-privacy.js — LITTLEG-KID-LOCATION-PRIVACY-04
 * New Friend Map rows prefer public Station Anchors and never persist a home address or home coordinate.
 * Legacy manual rows remain readable as approximate-only and keep the existing 200–300 m privacy transform.
 * Family Private social persistence is owned exclusively by LittleGFamilyData.socialGraph.
 *
 * Alignment contract:
 * - Runtime state owner: assets/littleg-kid-map.js
 * - Social persistence owner: LittleGFamilyData.socialGraph
 * - This file owns privacy location resolution, jitter transformation, and form privacy boundary hardening.
 */
(function (window, document) {
  'use strict';

  var VERSION = '1.3.0';
  var ROOT = '[data-littleg-kid-map]';
  var PRIVACY_VERSION = 3;
  var MIN_M = 200;
  var MAX_M = 300;
  var TARGET_M = 250;
  var SIGMA_M = 24;
  var EARTH_M = 6371008.8;
  var MAX_ROWS = 60;

  if (window.__LITTLEG_KID_LOCATION_PRIVACY__ === VERSION) return;
  window.__LITTLEG_KID_LOCATION_PRIVACY__ = VERSION;

  function q(root, selector) {
    return root ? root.querySelector(selector) : null;
  }

  function clean(value, max) {
    return String(value == null ? '' : value)
      .replace(/[\u0000-\u001f\u007f<>"'`]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, max || 180);
  }

  function finite(value) {
    var n = Number(value);
    return Number.isFinite(n) ? n : null;
  }

  function valid(lat, lng) {
    return lat !== null && lng !== null && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
  }

  function isChinese() {
    return /^zh\b/i.test(document.documentElement.lang || '');
  }

  function socialGraph() {
    var familyData = window.LittleGFamilyData;
    var graph = familyData && familyData.socialGraph;
    return graph &&
      typeof graph.listPlaces === 'function' &&
      typeof graph.getPlace === 'function' &&
      typeof graph.upsertPlace === 'function' &&
      typeof graph.replacePlaces === 'function'
      ? graph
      : null;
  }

  function readRows() {
    var graph = socialGraph();
    var rows = graph ? graph.listPlaces() : [];
    return Array.isArray(rows) ? rows.slice(0, MAX_ROWS) : [];
  }

  function writeRows(rows) {
    var graph = socialGraph();
    return !!graph && graph.replacePlaces(Array.isArray(rows) ? rows.slice(0, MAX_ROWS) : []);
  }

  function unit() {
    try {
      if (window.crypto && typeof window.crypto.getRandomValues === 'function') {
        var value = new Uint32Array(1);
        window.crypto.getRandomValues(value);
        return value[0] / 4294967296;
      }
    } catch (_) {}
    return Math.random();
  }

  function gaussian() {
    var u = Math.max(1e-12, unit());
    var v = Math.max(1e-12, unit());
    return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
  }

  function randomDistance() {
    for (var i = 0; i < 24; i += 1) {
      var d = TARGET_M + gaussian() * SIGMA_M;
      if (d >= MIN_M && d <= MAX_M) return d;
    }
    return TARGET_M;
  }

  function haversine(lat1, lng1, lat2, lng2) {
    var rad = Math.PI / 180;
    var dLat = (lat2 - lat1) * rad;
    var dLng = (lng2 - lng1) * rad;
    var a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * rad) * Math.cos(lat2 * rad) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
    return EARTH_M * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(Math.max(0, 1 - a)));
  }

  function destination(lat, lng, distance, bearing) {
    var phi1 = (lat * Math.PI) / 180;
    var lambda1 = (lng * Math.PI) / 180;
    var theta = (bearing * Math.PI) / 180;
    var delta = distance / EARTH_M;
    var sinPhi1 = Math.sin(phi1);
    var cosPhi1 = Math.cos(phi1);
    var sinDelta = Math.sin(delta);
    var cosDelta = Math.cos(delta);
    var phi2 = Math.asin(sinPhi1 * cosDelta + cosPhi1 * sinDelta * Math.cos(theta));
    var lambda2 =
      lambda1 +
      Math.atan2(Math.sin(theta) * sinDelta * cosPhi1, cosDelta - sinPhi1 * Math.sin(phi2));
    return { lat: (phi2 * 180) / Math.PI, lng: (((lambda2 * 180) / Math.PI + 540) % 360) - 180 };
  }

  function quantize(value) {
    return Number(Number(value).toFixed(3));
  }

  function jitter(lat, lng) {
    if (!valid(lat, lng)) return null;
    for (var i = 0; i < 96; i += 1) {
      var raw = destination(lat, lng, randomDistance(), unit() * 360);
      var point = { lat: quantize(raw.lat), lng: quantize(raw.lng) };
      var actual = haversine(lat, lng, point.lat, point.lng);
      if (actual >= MIN_M && actual <= MAX_M) {
        point.offsetM = Math.round(actual);
        return point;
      }
    }
    for (var bearing = 0; bearing < 360; bearing += 7) {
      var fallbackRaw = destination(lat, lng, TARGET_M, bearing + unit() * 6);
      var fallback = { lat: quantize(fallbackRaw.lat), lng: quantize(fallbackRaw.lng) };
      var fallbackDistance = haversine(lat, lng, fallback.lat, fallback.lng);
      if (fallbackDistance >= MIN_M && fallbackDistance <= MAX_M) {
        fallback.offsetM = Math.round(fallbackDistance);
        return fallback;
      }
    }
    return null;
  }

  function stripRaw(row) {
    if (!row || typeof row !== 'object') return row;
    [
      'exactLat',
      'exactLng',
      'exact_lat',
      'exact_lng',
      'rawLat',
      'rawLng',
      'raw_lat',
      'raw_lng',
      'originalLat',
      'originalLng',
      'original_lat',
      'original_lng',
      'sourceLat',
      'sourceLng',
      'source_lat',
      'source_lng',
      'homeLat',
      'homeLng',
      'home_lat',
      'home_lng',
      'exactAddress',
      'exact_address',
      'preciseLocation',
      'precise_location',
    ].forEach(function (key) {
      try {
        delete row[key];
      } catch (_) {}
    });
    return row;
  }

  function stationPoint(input) {
    var source = input && typeof input === 'object' ? input : {};
    var stationId = clean(source.stationId || source.station_id, 120);
    var lat = finite(source.stationLat != null ? source.stationLat : source.lat);
    var lng = finite(source.stationLng != null ? source.stationLng : source.lng);
    if (!stationId || !valid(lat, lng)) return null;
    return {
      locationType: 'station_anchor',
      stationId: stationId,
      stationName: clean(source.stationName || source.station_name, 120),
      stationNameZhHk: clean(source.stationNameZhHk || source.station_name_zh_hk, 120),
      stationLineIds: Array.isArray(source.stationLineIds || source.station_line_ids)
        ? (source.stationLineIds || source.station_line_ids)
            .slice(0, 8)
            .map(function (value) {
              return clean(value, 80);
            })
            .filter(Boolean)
        : [],
      lat: lat,
      lng: lng,
    };
  }

  function resolvePrivacyLocation(input) {
    var source = input && typeof input === 'object' ? input : {};
    if (clean(source.locationType || source.location_type, 80) === 'station_anchor') {
      return stationPoint(source);
    }
    var lat = finite(source.lat);
    var lng = finite(source.lng);
    if (!valid(lat, lng)) return null;
    var point = jitter(lat, lng);
    if (!point) return null;
    return {
      locationType: 'legacy_approximate',
      lat: point.lat,
      lng: point.lng,
      offsetM: point.offsetM,
      approximationMode: 'jitter_200_300m',
    };
  }

  function privateRow(row, point, mode, meta) {
    if (mode === 'station_anchor') {
      var station = stationPoint(Object.assign({}, row, meta || {}, point || {}));
      if (!station) return null;
      return stripRaw(
        Object.assign({}, row, {
          locationType: 'station_anchor',
          location_type: 'station_anchor',
          stationId: station.stationId,
          station_id: station.stationId,
          stationName: station.stationName,
          stationNameZhHk: station.stationNameZhHk,
          stationLineIds: station.stationLineIds,
          lat: station.lat,
          lng: station.lng,
          precision: 'station',
          privacyVersion: PRIVACY_VERSION,
          approximationMode: 'station_anchor',
          privacyRadiusM: null,
          jitterOffsetM: null,
          rawCoordinatesStored: false,
          visibility: 'family_private',
          publicSearchable: false,
          childPublicProfile: false,
          realtimeChild: false,
        })
      );
    }

    var next = Object.assign({}, row, {
      locationType: 'legacy_approximate',
      location_type: 'legacy_approximate',
      lat: point ? point.lat : row.lat,
      lng: point ? point.lng : row.lng,
      precision: 'approximate',
      privacyVersion: PRIVACY_VERSION,
      approximationMode: mode,
      rawCoordinatesStored: false,
      visibility: 'family_private',
      publicSearchable: false,
      childPublicProfile: false,
      realtimeChild: false,
    });

    if (mode === 'jitter_200_300m') {
      next.privacyRadiusM = MAX_M;
      next.jitterOffsetM = Number(point && point.offsetM ? point.offsetM : TARGET_M);
      delete next.landmarkId;
      delete next.landmarkLabel;
    } else {
      next.privacyRadiusM = null;
      next.jitterOffsetM = null;
      next.landmarkId = clean(meta && meta.landmarkId, 160);
      next.landmarkLabel = clean(meta && meta.landmarkLabel, 160);
      if (next.landmarkLabel) next.placeLabel = next.landmarkLabel;
    }
    return stripRaw(next);
  }

  function migrateLegacyRows() {
    var rows = readRows();
    var changed = false;
    var migrated = rows.map(function (row) {
      if (!row || typeof row !== 'object') return row;
      if (clean(row.locationType || row.location_type, 80) === 'station_anchor') {
        return stripRaw(Object.assign({}, row));
      }
      var lat = finite(row.lat);
      var lng = finite(row.lng);
      if (!valid(lat, lng)) {
        return stripRaw(
          Object.assign({}, row, {
            locationType: 'legacy_approximate',
            location_type: 'legacy_approximate',
          })
        );
      }
      if (
        Number(row.privacyVersion) >= 2 &&
        row.precision === 'approximate' &&
        row.rawCoordinatesStored === false &&
        (row.approximationMode === 'jitter_200_300m' ||
          row.approximationMode === 'landmark_centroid')
      ) {
        return stripRaw(
          Object.assign({}, row, {
            locationType: 'legacy_approximate',
            location_type: 'legacy_approximate',
          })
        );
      }
      var point = jitter(lat, lng);
      if (!point)
        return stripRaw(
          Object.assign({}, row, {
            locationType: 'legacy_approximate',
            location_type: 'legacy_approximate',
            precision: 'approximate',
            privacyVersion: PRIVACY_VERSION,
            rawCoordinatesStored: false,
          })
        );
      changed = true;
      return privateRow(row, point, 'jitter_200_300m');
    });
    if (changed) writeRows(migrated);
  }

  function landmarkDraft(root) {
    var place =
      root && root.__selectedPlace && typeof root.__selectedPlace === 'object'
        ? root.__selectedPlace
        : null;
    if (!place) return null;
    var lat = finite(place.lat);
    var lng = finite(place.lng);
    var id = clean(place.estate_id || place.place_id || place.id || place.key, 160);
    var label = clean(place.name_zh || place.name_en || place.name, 160);
    if (!valid(lat, lng) || !id || !label) return null;
    return {
      mode: 'landmark_centroid',
      lat: quantize(lat),
      lng: quantize(lng),
      landmarkId: id,
      landmarkLabel: label,
    };
  }

  function pickerDraft(root) {
    var picked =
      root && root.__kidPicked && typeof root.__kidPicked === 'object' ? root.__kidPicked : null;
    var lat = finite(picked && picked.lat);
    var lng = finite(picked && picked.lng);
    return valid(lat, lng) ? { mode: 'jitter_200_300m', lat: lat, lng: lng } : null;
  }

  function selectedStationDraft(root) {
    var selected =
      root && root.__kidSelectedStation && typeof root.__kidSelectedStation === 'object'
        ? root.__kidSelectedStation
        : null;
    var station = stationPoint(selected);
    return station ? Object.assign({ mode: 'station_anchor' }, station) : null;
  }

  function storedRow(id) {
    var key = clean(id, 120);
    var graph = socialGraph();
    if (!key || !graph) return null;
    var row = graph.getPlace(key);
    return row && typeof row === 'object' ? row : null;
  }

  function setPrivacyUi(form, mode) {
    if (!form) return;
    var stationMode = mode === 'station_anchor';
    var precision = q(form, '[data-kid-precision]');

    if (precision) {
      if (precision.tagName === 'SELECT') {
        Array.prototype.slice.call(precision.options || []).forEach(function (option) {
          if (option.value !== 'approximate' && option.value !== 'station') option.remove();
        });
        precision.value = stationMode ? 'station' : 'approximate';
      } else {
        precision.value = stationMode ? 'station' : 'approximate';
      }
    }

    var note = q(form, '[data-kid-privacy-selector], [data-kid-station-privacy]');
    if (note) {
      note.textContent = stationMode
        ? isChinese()
          ? '🔒 只保存所選港鐵站，不保存住宅地址或住宅座標。'
          : '🔒 Station only. No home address or home coordinate is stored.'
        : isChinese()
          ? '不公開搜尋；舊資料只保存 200–300 米隱私偏移點，或已核實公共地標中心。'
          : 'Not publicly searchable. Legacy rows keep only a 200–300 m privacy-offset point or a verified public landmark centre.';
    }
  }

  function prepareMode(event) {
    var target = event.target && event.target.closest ? event.target.closest('button,a') : null;
    if (!target) return;
    var root = target.closest(ROOT);
    if (!(root instanceof HTMLElement)) return;

    if (target.matches('[data-kid-station-picker], [data-kid-station-result]')) {
      root.__kidLocationPrivacyDraft = selectedStationDraft(root) || { mode: 'station_anchor' };
      window.setTimeout(function () {
        setPrivacyUi(q(root, '[data-kid-map-add-form]'), 'station_anchor');
      }, 0);
    } else if (target.matches('[data-kid-mark-selected]')) {
      root.__kidLocationPrivacyDraft = landmarkDraft(root) || { mode: 'jitter_200_300m' };
      window.setTimeout(function () {
        var form = q(root, '[data-kid-map-add-form]');
        setPrivacyUi(form, root.__kidLocationPrivacyDraft.mode);
        if (root.__kidLocationPrivacyDraft.mode === 'landmark_centroid') {
          var placeLabel = q(form, '[data-kid-place-label]');
          if (placeLabel) placeLabel.value = root.__kidLocationPrivacyDraft.landmarkLabel || '';
        }
      }, 0);
    } else if (target.matches('[data-kid-map-action="confirm-pin"]')) {
      root.__kidLocationPrivacyDraft = pickerDraft(root) || { mode: 'jitter_200_300m' };
      window.setTimeout(function () {
        setPrivacyUi(q(root, '[data-kid-map-add-form]'), 'jitter_200_300m');
      }, 0);
    } else if (target.matches('[data-kid-friend-edit]')) {
      root.__kidLocationPrivacyDraft = { mode: 'preserve', id: clean(root.__kidOpenSocialId, 120) };
      window.setTimeout(function () {
        var row = storedRow(root.__kidOpenSocialId);
        setPrivacyUi(
          q(root, '[data-kid-map-add-form]'),
          row && row.locationType === 'station_anchor'
            ? 'station_anchor'
            : row && row.approximationMode
              ? row.approximationMode
              : 'jitter_200_300m'
        );
      }, 0);
    } else if (target.matches('[data-kid-add-kind]')) {
      root.__kidLocationPrivacyDraft = selectedStationDraft(root) || { mode: 'station_anchor' };
    }
  }

  function protectSubmit(event) {
    var form = event.target;
    if (!form || !form.matches || !form.matches('[data-kid-map-add-form]')) return;
    var root = form.closest(ROOT);
    if (!(root instanceof HTMLElement)) return;

    var idInput = q(form, '[data-kid-annotation-id]');
    var latInput = q(form, '[data-kid-annotation-lat]');
    var lngInput = q(form, '[data-kid-annotation-lng]');
    if (!latInput || !lngInput) return;

    var id = clean(idInput && idInput.value, 120);
    var old = id ? storedRow(id) : null;
    var draft = selectedStationDraft(root) || root.__kidLocationPrivacyDraft || null;
    var stationIdInput = q(form, '[data-kid-station-id]');
    var wantsStation =
      !!clean(stationIdInput && stationIdInput.value, 120) ||
      !!(draft && draft.mode === 'station_anchor' && selectedStationDraft(root));
    var mode = wantsStation
      ? 'station_anchor'
      : draft && draft.mode
        ? draft.mode
        : 'jitter_200_300m';
    var meta = { id: id, mode: mode };

    if (mode === 'station_anchor') {
      var station = selectedStationDraft(root);
      if (station) {
        latInput.value = String(station.lat);
        lngInput.value = String(station.lng);
        meta = Object.assign(meta, station);
      }
    } else if (
      mode === 'preserve' &&
      old &&
      clean(old.locationType || old.location_type, 80) === 'station_anchor'
    ) {
      var oldStation = stationPoint(old);
      if (oldStation) {
        latInput.value = String(oldStation.lat);
        lngInput.value = String(oldStation.lng);
        meta = Object.assign(meta, oldStation, { mode: 'station_anchor' });
      }
    } else if (
      mode === 'preserve' &&
      old &&
      Number(old.privacyVersion) >= 2 &&
      old.rawCoordinatesStored === false
    ) {
      latInput.value = String(old.lat);
      lngInput.value = String(old.lng);
      meta.mode = old.approximationMode || 'jitter_200_300m';
      meta.offsetM = old.jitterOffsetM || null;
      meta.landmarkId = old.landmarkId || '';
      meta.landmarkLabel = old.landmarkLabel || '';
    } else if (mode === 'landmark_centroid' && draft) {
      latInput.value = String(draft.lat);
      lngInput.value = String(draft.lng);
      meta.landmarkId = draft.landmarkId || '';
      meta.landmarkLabel = draft.landmarkLabel || '';
      var placeLabel = q(form, '[data-kid-place-label]');
      if (placeLabel && draft.landmarkLabel) placeLabel.value = draft.landmarkLabel;
    } else {
      var rawLat = finite(draft && draft.lat);
      var rawLng = finite(draft && draft.lng);
      if (!valid(rawLat, rawLng)) {
        rawLat = finite(latInput.value);
        rawLng = finite(lngInput.value);
      }
      if (valid(rawLat, rawLng)) {
        var point = jitter(rawLat, rawLng);
        if (point) {
          latInput.value = String(point.lat);
          lngInput.value = String(point.lng);
          meta.mode = 'jitter_200_300m';
          meta.offsetM = point.offsetM;
        }
      }
    }

    var precision = q(form, '[data-kid-precision]');
    if (precision) precision.value = mode === 'station_anchor' ? 'station' : 'approximate';
    setPrivacyUi(form, meta.mode);
  }

  function hardenUi() {
    Array.prototype.slice
      .call(document.querySelectorAll('[data-kid-map-add-form]'))
      .forEach(function (form) {
        setPrivacyUi(form, q(form, '[data-kid-station-id]') ? 'station_anchor' : 'jitter_200_300m');
      });
  }

  migrateLegacyRows();
  document.addEventListener('click', prepareMode, true);
  document.addEventListener('submit', protectSubmit, true);
  if (document.readyState === 'loading')
    document.addEventListener('DOMContentLoaded', hardenUi, { once: true });
  else hardenUi();
  document.addEventListener('shopify:section:load', function () {
    migrateLegacyRows();
    hardenUi();
  });

  window.LittleGKidLocationPrivacy = Object.freeze({
    version: VERSION,
    defaultStrategy: 'station_anchor',
    storage: 'station-anchor-or-legacy-approximate',
    storageOwner: 'LittleGFamilyData.socialGraph',
    modes: Object.freeze(['station_anchor', 'jitter_200_300m', 'landmark_centroid']),
    rawCoordinatePersistence: false,
    rawHomeCoordinatePersistence: false,
    resolvePrivacyLocation: resolvePrivacyLocation,
    jitterMinM: MIN_M,
    jitterMaxM: MAX_M,
  });
})(window, document);
